import { PartTypeSchema, PartTypeType } from "../types/PrismaZod";
import { FormatterMaterialParameter } from "./formatMaterialDimensions";

export type MaterialDimensionField = keyof FormatterMaterialParameter;

const MATERIAL_DIMENSION_FIELDS: MaterialDimensionField[] = [
  "height",
  "width",
  "length",
  "tubeThickness",
];

const NO_DISCRIMINANT_DIMENSION: MaterialDimensionField[] = [];
const SECTION_DISCRIMINANT_DIMENSIONS: MaterialDimensionField[] = [
  "height",
  "width",
  "tubeThickness",
];

const DISCRIMINANT_MATERIAL_DIMENSIONS: Record<PartTypeType, MaterialDimensionField[]> = {
  [PartTypeSchema.enum.SHEET]: ["height"],
  [PartTypeSchema.enum.MACHINING]: NO_DISCRIMINANT_DIMENSION,
  [PartTypeSchema.enum.ASSEMBLY]: NO_DISCRIMINANT_DIMENSION,
  [PartTypeSchema.enum.CONSUMABLE]: NO_DISCRIMINANT_DIMENSION,
  [PartTypeSchema.enum.OTHER]: NO_DISCRIMINANT_DIMENSION,
  [PartTypeSchema.enum.ROUND_BAR]: NO_DISCRIMINANT_DIMENSION,
  [PartTypeSchema.enum.SQUARE_BAR]: NO_DISCRIMINANT_DIMENSION,
  [PartTypeSchema.enum.ROUND_TUBE]: SECTION_DISCRIMINANT_DIMENSIONS,
  [PartTypeSchema.enum.SQUARE_TUBE]: SECTION_DISCRIMINANT_DIMENSIONS,
  [PartTypeSchema.enum.RECTANGULAR_TUBE]: SECTION_DISCRIMINANT_DIMENSIONS,
  [PartTypeSchema.enum.PROFILE]: SECTION_DISCRIMINANT_DIMENSIONS,
};

export const getDiscriminantMaterialDimensions = (
  partType: PartTypeType | null | undefined,
): MaterialDimensionField[] =>
  partType ? DISCRIMINANT_MATERIAL_DIMENSIONS[partType] : NO_DISCRIMINANT_DIMENSION;

export const isDiscriminantMaterialDimension = (
  partType: PartTypeType | null | undefined,
  field: MaterialDimensionField,
): boolean => getDiscriminantMaterialDimensions(partType).includes(field);

const roundDimension = (dimension: number) => parseFloat(dimension.toFixed(10));

export const doesMaterialDimensionFit = ({
  partType,
  field,
  partDimension,
  materialDimension,
  tolerance = 0,
}: {
  partType: PartTypeType | null | undefined;
  field: MaterialDimensionField;
  partDimension: number | null | undefined;
  materialDimension: number | null | undefined;
  tolerance?: number;
}): boolean => {
  if (partDimension === null || partDimension === undefined) {
    return true;
  }

  if (materialDimension === null || materialDimension === undefined) {
    return false;
  }

  if (!isDiscriminantMaterialDimension(partType, field)) {
    return materialDimension >= partDimension;
  }

  return Math.abs(roundDimension(materialDimension - partDimension)) <= tolerance;
};

export const doesMaterialFitPartDimensions = ({
  partType,
  partDimensions,
  materialDimensions,
}: {
  partType: PartTypeType | null | undefined;
  partDimensions: FormatterMaterialParameter;
  materialDimensions: FormatterMaterialParameter;
}): boolean =>
  MATERIAL_DIMENSION_FIELDS.every((field) =>
    doesMaterialDimensionFit({
      partType,
      field,
      partDimension: partDimensions[field],
      materialDimension: materialDimensions[field],
    }),
  );
